import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { LineChart, PieChart, BarChart2, Wallet, Shield } from 'lucide-react';

// User provided stock chart image
const STOCK_CHART_IMAGE = "https://customer-assets.emergentagent.com/job_f7918f01-bf20-4cfe-88f8-06dc16353ee9/artifacts/iij52lkv_download%20%282%29.jpg";

// User provided stock market loss/bearish image
const BEARISH_IMAGE = "https://customer-assets.emergentagent.com/job_f7918f01-bf20-4cfe-88f8-06dc16353ee9/artifacts/2g32rgdo_Illustration%20of%20stock%20market%20losses_%20Bankrupt%20financial%20information%20concept%20with%20arrows%20pointin.jpg";

const services = [
  {
    icon: LineChart,
    title: 'Market Analysis',
    description: 'Get comprehensive market analysis from top financial experts. Track trends, patterns, and opportunities in real-time.',
    image: STOCK_CHART_IMAGE,
  },
  {
    icon: PieChart,
    title: 'Portfolio Insights',
    description: 'AI-assisted portfolio tracking and insights. Understand your investments better with smart summaries.',
    image: BEARISH_IMAGE,
  },
];

const additionalServices = [
  {
    icon: BarChart2,
    title: 'Technical Analysis',
    description: 'Advanced charting tools and technical indicators for informed trading decisions.',
  },
  {
    icon: Wallet,
    title: 'Investment Planning',
    description: 'Smart investment planning resources for both short-term trading and long-term wealth building.',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    description: 'Learn risk management strategies to protect your portfolio during market volatility.',
  },
];

const ServicesSection = () => {
  return (
    <section id="features" className="py-20 bg-muted/30">
      {/* Decorative dots pattern */}
      <div className="absolute left-0 top-0 w-32 opacity-20">
        <div className="grid grid-cols-6 gap-2">
          {[...Array(36)].map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-primary"></div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="section-subtitle">Our Services</p>
          <h2 className="section-title text-foreground">What We Do</h2>
          <div className="w-16 h-1 bg-primary mx-auto mt-4"></div>
        </div>

        {/* Main Services with Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 max-w-4xl mx-auto">
          {services.map((service, index) => (
            <Card 
              key={service.title} 
              className="news-card overflow-hidden group flex flex-col"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <service.icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">{service.title}</h3>
              </CardHeader>
              <CardContent className="flex-1 pb-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Services */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {additionalServices.map((service) => (
            <Card 
              key={service.title} 
              className="feature-card-alt group hover:shadow-lg transition-shadow flex flex-col"
            >
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary transition-colors">
                  <service.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{service.title}</h3>
              </CardHeader>
              <CardContent className="flex-1 pb-6">
                <p className="text-muted-foreground text-sm">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
